// src/components/sampleorders/SampleOrderList.js
// ✅ FIXED: Matches Kanban visibility exactly (mine-only for non-admin staff)
// ✅ UPDATE: Default tab = All for Admin/Owner, otherwise Assigned
// ✅ UPDATE: Items stack (one per line)
// ✅ UPDATE: Meta row stays on ONE line
// ✅ UPDATE: Attachment icon + counts (header + per order)
// ✅ NEW STANDARD: sample order identity uses createdBy / createdByName / createdByRole (NO requestedBy, NO legacy)

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Package,
  Search,
  X,
  ChevronRight,
  ChevronDown,
  User,
  ShoppingCart,
  Box,
  Paperclip,
} from "lucide-react";

// NOTE: we still reuse your role helper from sampleOrderVisibility.js
import { isOwnerOrAdminRole } from "../../utils/sampleOrderVisibility";
import { getSampleOrderStatusLabel } from "../../utils/sampleOrderStatusLabels";

const STATUS_GROUPS = [
  "Pending",
  "Approved",
  "Preparation",
  "Shipped",
  "Delivered",
  "Follow Up",
  "Completed",
  "Converted To Order",
  "Cancelled",
];

const mapStatusToGroup = (status) => {
  const s = status || "Pending";
  if (s === "Received") return "Delivered";
  return STATUS_GROUPS.includes(s) ? s : "Other";
};

const getStatusColor = (status, isConverted) => {
  if (isConverted) return "bg-purple-100 text-purple-700 border-purple-200";

  switch (status) {
    case "Approved":
    case "Delivered":
    case "Completed":
      return "bg-emerald-100 text-emerald-700 border-emerald-200";
    case "Shipped":
      return "bg-blue-100 text-blue-700 border-blue-200";
    case "Follow Up":
      return "bg-orange-100 text-orange-700 border-orange-200";
    case "Cancelled":
      return "bg-red-100 text-red-700 border-red-200";
    default:
      return "bg-amber-100 text-amber-700 border-amber-200";
  }
};

// -------------------------
// Item helpers
// -------------------------
const safeArray = (v) => (Array.isArray(v) ? v : []);

const toQty = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return 1;
  return Number.isInteger(n) ? n : n;
};

const getItemName = (it) => {
  if (!it) return "Item";
  return (
    it.productName ||
    it.name ||
    it.itemName ||
    it.title ||
    it.productCode ||
    it.sku ||
    it.code ||
    "Item"
  );
};

const getItemQty = (it) => {
  if (!it) return 1;
  return toQty(it.quantity ?? it.qty ?? it.count ?? 1);
};

const getNormalizedOrderItems = (order) => {
  const rawItems = safeArray(order?.items);

  // ✅ NEW STANDARD: prefer items[] only
  let items = rawItems
    .map((it) => ({
      name: String(getItemName(it)).trim() || "Item",
      qty: getItemQty(it),
    }))
    .filter((x) => x.name && x.name !== "Item");

  if (rawItems.length > 0 && items.length === 0) {
    items = rawItems.map((it) => ({
      name: String(getItemName(it)).trim() || "Item",
      qty: getItemQty(it),
    }));
  }

  // ✅ NO legacy single-item fallback (productName/quantity on order) — new standard only
  // If items[] are missing, it will show "No items"

  // consolidate duplicates by name
  const map = new Map();
  items.forEach((it) => {
    const key = it.name;
    map.set(key, (map.get(key) || 0) + toQty(it.qty));
  });

  return Array.from(map.entries()).map(([name, qty]) => ({ name, qty }));
};

const getDistinctItemCount = (order) => getNormalizedOrderItems(order).length;

const getTotalItemQty = (order) =>
  getNormalizedOrderItems(order).reduce((sum, it) => sum + toQty(it.qty), 0);

const formatDeliveryDate = (order) => {
  const raw = order.deliveryDate || order.requestedDate;
  if (!raw) return null;

  let dateObj = null;
  if (raw && typeof raw.toDate === "function") {
    dateObj = raw.toDate();
  } else {
    const parsed = new Date(raw);
    if (!isNaN(parsed.getTime())) dateObj = parsed;
  }

  if (!dateObj) return null;
  return dateObj.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const getAttachmentCount = (order) => {
  const direct =
    order?.attachmentCount ??
    order?.attachmentsCount ??
    order?.attachmentsTotal ??
    order?.filesCount ??
    null;

  if (typeof direct === "number" && Number.isFinite(direct) && direct >= 0) {
    return direct;
  }

  const candidates = [
    order?.attachments,
    order?.files,
    order?.attachmentIds,
    order?.attachmentURLs,
    order?.attachmentUrls,
  ];

  for (const c of candidates) {
    if (Array.isArray(c)) return c.length;
  }

  return 0;
};

// -------------------------
// Visibility helpers (MATCH KANBAN) — NEW STANDARD ONLY
// -------------------------
const normId = (v) => {
  if (!v) return null;
  if (typeof v === "string") return v;
  if (typeof v === "object") return v.id || v.uid || v.userId || v.value || null;
  return String(v);
};

const normalizeRole = (role) =>
  String(role || "")
    .trim()
    .toLowerCase()
    .replace(/[_\s-]+/g, "");

// -------------------------
// Component
// -------------------------
const SampleOrderList = ({
  requests = [],
  companyUsers = [],
  currentUserId,
  currentUserRole,
  currentUserAccountId, // optional (customers)
  currentUserAccountIds = [],
  onSelectRequest,
  headerAction = null,
  sampleStatusLabelSettings = {},
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [desktopStatusMenuOpen, setDesktopStatusMenuOpen] = useState(false);
  const [createdByMeOnly, setCreatedByMeOnly] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState(
    () => new Set(["Completed", "Cancelled", "Converted to Order", "Converted To Order"])
  );
  const desktopStatusMenuRef = useRef(null);

  const canSeeAll = isOwnerOrAdminRole(currentUserRole);
  const isCustomer = normalizeRole(currentUserRole) === "customer";

  useEffect(() => {
    if (!desktopStatusMenuOpen) return undefined;

    const handlePointerDown = (event) => {
      if (desktopStatusMenuRef.current?.contains(event.target)) return;
      setDesktopStatusMenuOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [desktopStatusMenuOpen]);

  /**
   * ✅ Visibility (NEW STANDARD ONLY):
   * - Admin/Owner: all not-hidden
   * - Customer: account match OR createdBy === me
   * - Non-admin staff: mine OR linked to assigned accounts
   */
  const visibleRequests = useMemo(() => {
    const arr = Array.isArray(requests) ? requests : [];
    const me = normId(currentUserId);
    const roleNorm = normalizeRole(currentUserRole);
    const visibleAccountIdSet = new Set(
      (Array.isArray(currentUserAccountIds) ? currentUserAccountIds : [])
        .map((id) => normId(id))
        .filter(Boolean)
    );

    const notHidden = (o) => !o?.hidden;

    // Admin/Owner: all not-hidden
    if (canSeeAll) return arr.filter(notHidden);

    // Non-admin must have user id
    if (!me) return [];

    // Customer: account match OR createdBy === me
    if (roleNorm === "customer") {
      const acct = normId(currentUserAccountId);
      return arr.filter((o) => {
        if (!notHidden(o)) return false;
        const createdBy = normId(o?.createdBy);
        const orderAccountId = normId(o?.accountId);
        return (acct && orderAccountId && orderAccountId === acct) || createdBy === me;
      });
    }

    // Staff (enduser): mine or account-linked
    return arr.filter((o) => {
      if (!notHidden(o)) return false;
      const createdBy = normId(o?.createdBy);
      const assignedToId = normId(o?.assignedToId) || normId(o?.assignedUserId) || normId(o?.assignedTo);
      const accountId = normId(o?.accountId);
      const locationId = normId(o?.locationId);
      const accountIds = Array.isArray(o?.accountIds) ? o.accountIds.map((id) => normId(id)).filter(Boolean) : [];
      return (
        createdBy === me ||
        assignedToId === me ||
        (accountId && visibleAccountIdSet.has(accountId)) ||
        (locationId && visibleAccountIdSet.has(locationId)) ||
        accountIds.some((id) => visibleAccountIdSet.has(id))
      );
    });
  }, [requests, currentUserId, currentUserRole, currentUserAccountId, currentUserAccountIds, canSeeAll]);

  // Optional "created by me" filter
  const ownershipFilteredRequests = useMemo(() => {
    const arr = Array.isArray(visibleRequests) ? visibleRequests : [];
    const me = normId(currentUserId);
    if (!createdByMeOnly || !me) return arr;
    return arr.filter((o) => normId(o?.createdBy) === me);
  }, [visibleRequests, createdByMeOnly, currentUserId]);

  // Search filter
  const searchFiltered = useMemo(() => {
    const searchLower = (searchTerm || "").toLowerCase().trim();
    if (!searchLower) return ownershipFilteredRequests;

    return ownershipFilteredRequests.filter((order) => {
      const itemNames = safeArray(order.items)
        .map((it) => String(getItemName(it) || ""))
        .join(" ");

      const createdByName = (order?.createdByName || "").toString();
      const createdByRole = (order?.createdByRole || "").toString();

      return (
        (order.accountName || "").toLowerCase().includes(searchLower) ||
        (order.status || "").toLowerCase().includes(searchLower) ||
        itemNames.toLowerCase().includes(searchLower) ||
        createdByName.toLowerCase().includes(searchLower) ||
        createdByRole.toLowerCase().includes(searchLower)
      );
    });
  }, [ownershipFilteredRequests, searchTerm]);

  // Status filter
  const filteredRequests = useMemo(() => {
    return searchFiltered.filter((order) => {
      if (!selectedStatuses.length) return true;
      return selectedStatuses.some((status) => {
        if (status === "Converted To Order") return !!order.convertedToOrder;
        return mapStatusToGroup(order.status) === status;
      });
    });
  }, [searchFiltered, selectedStatuses]);

  // Count by status (based on searchFiltered so chips remain stable)
  const statusCounts = useMemo(() => {
    const counts = {};
    STATUS_GROUPS.forEach((status) => {
      counts[status] = searchFiltered.filter((order) => {
        if (status === "Converted To Order") return !!order.convertedToOrder;
        return mapStatusToGroup(order.status) === status;
      }).length;
    });
    return counts;
  }, [searchFiltered]);

  const totalAttachmentsForView = useMemo(() => {
    return (filteredRequests || []).reduce((sum, o) => sum + getAttachmentCount(o), 0);
  }, [filteredRequests]);

  const groupedRequests = useMemo(() => {
    const groups = new Map();
    STATUS_GROUPS.forEach((status) => groups.set(status, []));
    groups.set("Other", []);

    (filteredRequests || []).forEach((order) => {
      const key = order.convertedToOrder ? "Converted To Order" : mapStatusToGroup(order.status);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(order);
    });

    return Array.from(groups.entries()).filter(([, items]) => items.length > 0);
  }, [filteredRequests]);

  const toggleSection = (section) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  };

  const getAssignedName = (order) => {
    const assignedToId = normId(order?.assignedToId) || null;

    const user = companyUsers.find((u) => String(u?.id || u?.uid) === String(assignedToId));
    if (user) {
      const name = user.fullName || user.displayName || user.name || user.email || "";
      const parts = name.split(/\s+/);
      return parts.length > 1 ? parts[parts.length - 1] : name.split("@")[0];
    }
    return order.assignedToName || "Unassigned";
  };

  const getCreatorLabel = (order) => {
    const name = (order?.createdByName || "").toString().trim();
    const role = (order?.createdByRole || "").toString().trim().toLowerCase();
    if (!name && !role) return null;
    if (name && role) return `${name} (${role})`;
    return name || role;
  };

  const toggleDesktopStatus = (status) => {
    setSelectedStatuses((prev) =>
      prev.includes(status) ? prev.filter((value) => value !== status) : [...prev, status]
    );
  };

  const desktopStatusLabel =
    selectedStatuses.length === 0
      ? `All statuses (${searchFiltered.length})`
      : selectedStatuses.length === 1
      ? `${selectedStatuses[0]} (${statusCounts[selectedStatuses[0]] || 0})`
      : `${selectedStatuses.length} statuses selected`;

  return (
    <div className="bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100 h-full flex flex-col overflow-visible">
      {/* Header */}
      <div className="relative z-40 overflow-visible px-4 py-3 border-b border-slate-200/60 bg-white/70 backdrop-blur-sm">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm">
              <Package className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 text-sm">
                {isCustomer ? "My Sample Orders" : "Sample Requests"}
              </h3>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <span>{filteredRequests.length} total</span>
                <span className="text-slate-300">•</span>
                <span className="inline-flex items-center gap-1">
                  <Paperclip className="w-3.5 h-3.5 text-slate-500" />
                  {totalAttachmentsForView}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-2 flex items-center gap-2">
          <div className="relative min-w-0 flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input
              type="text"
              placeholder="Search requests..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-8 pr-8 py-2 text-xs border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {headerAction ? <div className="shrink-0">{headerAction}</div> : null}
        </div>

        <div className="mb-1 flex items-center gap-2">
          {currentUserId ? (
            <label className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-[11px] font-medium text-slate-600">
              <input
                type="checkbox"
                checked={createdByMeOnly}
                onChange={(e) => setCreatedByMeOnly(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-slate-300"
              />
              Created by me
            </label>
          ) : null}

          <div className="min-w-0 flex-1 md:hidden">
            <select
              value={selectedStatuses.length === 1 ? selectedStatuses[0] : "all"}
              onChange={(e) => setSelectedStatuses(e.target.value === "all" ? [] : [e.target.value])}
              className="h-9 w-full rounded-lg border border-slate-300 bg-white px-3 text-xs font-medium text-slate-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All ({searchFiltered.length})</option>
              {STATUS_GROUPS.map((status) => {
                const count = statusCounts[status];
                if (!count) return null;
                return (
                  <option key={status} value={status}>
                    {status} ({count})
                  </option>
                );
              })}
            </select>
          </div>

          <div ref={desktopStatusMenuRef} className="relative hidden md:block">
            <button
              type="button"
              onClick={() => setDesktopStatusMenuOpen((prev) => !prev)}
              className="inline-flex h-9 min-w-[220px] items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 text-[11px] font-medium text-slate-700 shadow-sm hover:bg-slate-50"
            >
              <span className="truncate">{desktopStatusLabel}</span>
              <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${desktopStatusMenuOpen ? "rotate-180" : ""}`} />
            </button>

            {desktopStatusMenuOpen ? (
              <div className="absolute left-0 top-full z-[90] mt-2 w-[280px] rounded-2xl border border-slate-200 bg-white p-2 shadow-2xl">
                <button
                  type="button"
                  onClick={() => setSelectedStatuses([])}
                  className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-xs font-medium text-slate-700 hover:bg-slate-50"
                >
                  <span>All statuses</span>
                  <span className="text-slate-400">{searchFiltered.length}</span>
                </button>
                <div className="my-2 border-t border-slate-100" />
                <div className="max-h-64 space-y-1 overflow-y-auto">
                  {STATUS_GROUPS.map((status) => {
                    const count = statusCounts[status];
                    return (
                      <label
                        key={status}
                        className={`flex cursor-pointer items-center justify-between rounded-xl px-3 py-2 text-xs ${
                          count ? "text-slate-700 hover:bg-slate-50" : "text-slate-400"
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={selectedStatuses.includes(status)}
                            onChange={() => toggleDesktopStatus(status)}
                            className="h-3.5 w-3.5 rounded border-slate-300"
                          />
                          {status}
                        </span>
                        <span>{count || 0}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="relative z-0 flex-1 overflow-y-auto p-3">
        {filteredRequests.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center mb-3 shadow-inner">
              <Package className="w-6 h-6 text-slate-500" />
            </div>
            <h4 className="text-xs font-medium text-slate-900 mb-1">No requests found</h4>
            <p className="text-xs text-slate-500">
              {searchTerm ? "Try adjusting your search" : "New requests will appear here"}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {groupedRequests.map(([section, sectionOrders]) => {
              const isCollapsed = collapsedSections.has(section);
              const sectionTone =
                section === "Completed"
                  ? "border-emerald-200 bg-emerald-50"
                  : section === "Cancelled"
                  ? "border-rose-200 bg-rose-50"
                  : section === "Follow Up"
                  ? "border-orange-200 bg-orange-50"
                  : section === "Converted To Order"
                  ? "border-purple-200 bg-purple-50"
                  : "border-slate-200 bg-slate-50";

              return (
                <div key={section} className={`rounded-2xl border ${sectionTone} overflow-visible shadow-sm`}>
                  <button
                    type="button"
                    onClick={() => toggleSection(section)}
                    className="flex w-full items-center justify-between gap-3 px-3 py-3 text-left"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs font-semibold text-slate-900 truncate">{section}</span>
                      <span className="inline-flex items-center rounded-full bg-white/80 px-2 py-0.5 text-[11px] font-semibold text-slate-600 border border-white/70">
                        {sectionOrders.length}
                      </span>
                    </div>
                    <ChevronDown
                      className={`w-4 h-4 text-slate-500 transition-transform ${isCollapsed ? "" : "rotate-180"}`}
                    />
                  </button>

                  {!isCollapsed && (
                    <div className="space-y-2 border-t border-white/70 bg-white/40 p-2.5">
                      {sectionOrders.map((order) => {
              const normalizedItems = getNormalizedOrderItems(order);
              const distinctItemCount = getDistinctItemCount(order);
              const totalQty = getTotalItemQty(order);

              const deliveryDate = formatDeliveryDate(order);
              const assignedName = getAssignedName(order);
              const creatorLabel = getCreatorLabel(order);

              const statusColor = getStatusColor(order.status, !!order.convertedToOrder);
              const displayStatus = order.convertedToOrder
                ? getSampleOrderStatusLabel("Converted to Order", sampleStatusLabelSettings)
                : getSampleOrderStatusLabel(order.status || "Pending", sampleStatusLabelSettings);

              const attachmentCount = getAttachmentCount(order);

              const shownItems = normalizedItems.slice(0, 2);
              const remaining = normalizedItems.length - shownItems.length;

              return (
                <button
                  key={order.id}
                  onClick={() => onSelectRequest && onSelectRequest(order)}
                  className="w-full text-left bg-white/95 backdrop-blur-sm border border-slate-200/90 rounded-2xl p-3 hover:border-blue-300 hover:shadow-md hover:bg-white transition-all group"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <h4 className="font-semibold text-slate-900 text-sm truncate">
                              {order.accountName || "No Account"}
                            </h4>
                            {order.convertedToOrder && (
                              <ShoppingCart className="w-3 h-3 text-purple-500 flex-shrink-0" />
                            )}
                          </div>
                          <div className="mt-1 text-[11px] text-slate-500 truncate">
                            #{order.id?.slice?.(0, 8) || "sample"} {deliveryDate ? `• Delivers ${deliveryDate}` : ""}
                          </div>
                        </div>

                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium border ${statusColor}`}
                          >
                            {displayStatus}
                          </span>
                          <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-blue-600 transition-colors" />
                        </div>
                      </div>

                      {/* Items stacked */}
                      <div className="space-y-0.5 mb-2">
                        {shownItems.length === 0 ? (
                          <p className="text-xs text-slate-500">No items</p>
                        ) : (
                          <>
                            {shownItems.map((it, idx) => (
                              <p
                                key={`${it.name}-${idx}`}
                                className="text-xs text-slate-700 truncate"
                                title={`${it.qty}× ${it.name}`}
                              >
                                {it.qty}× {it.name}
                              </p>
                            ))}
                            {remaining > 0 && (
                              <p className="text-[11px] text-slate-500">+{remaining} more</p>
                            )}
                          </>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                        <div className="inline-flex items-center gap-1.5 min-w-0">
                          <User className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="truncate max-w-[140px]" title={assignedName}>
                            {assignedName}
                          </span>
                        </div>

                        {creatorLabel && (
                          <div className="inline-flex items-center gap-1.5 min-w-0">
                            <User className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="truncate max-w-[160px]" title={creatorLabel}>
                              {creatorLabel}
                            </span>
                          </div>
                        )}

                        {distinctItemCount > 0 && (
                          <div
                            className="inline-flex items-center gap-1.5 flex-shrink-0"
                            title={`${distinctItemCount} item(s) · ${totalQty} total qty`}
                          >
                            <Box className="w-3.5 h-3.5" />
                            <span className="whitespace-nowrap">
                              {distinctItemCount} item{distinctItemCount === 1 ? "" : "s"} · Qty{" "}
                              {totalQty}
                            </span>
                          </div>
                        )}

                        {attachmentCount > 0 && (
                          <div className="inline-flex items-center gap-1.5 flex-shrink-0">
                            <Paperclip className="w-3.5 h-3.5" />
                            <span className="whitespace-nowrap">{attachmentCount}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SampleOrderList;
